import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import { log } from '@livekit/components-core'

import { useAppSelector, useAppDispatch } from '@/src/store/hooks'
import { selectPhone, selectReferrer, setPhone, setStatus } from '@/src/store/slices/room'
import { clearAccessToken, setAccessToken } from '@/src/store/slices/token'
import { setIsAdmin } from '@/src/store/slices/meetData'
import {ORGANIZER_VERIFICATION_REQUIRED, requireOrganizerVerification} from '@/src/lib/organizerVerification'
import {getInterviewAdmissionFailure, shouldCheckInterviewLimit, type InterviewAdmissionFailure} from '@/src/lib/interviewAdmission'

/** @public */
export interface UserInfo {
  identity?: string
  name?: string
  metadata?: string
  phone?: string
  [key: string]: string | number | boolean | null | undefined
}

/** @public */
export interface UseTokenOptions {
  userInfo?: UserInfo
  onAdmissionDenied?: (reason: InterviewAdmissionFailure) => void
}

/**
 * The `useToken` hook fetches a token from the given token endpoint with the given user info.
 *
 * @example
 * ```tsx
 * const token = useToken(<token-endpoint>, roomName, { userInfo: { identity, name }});
 * ```
 * @public */
export function useToken(
  tokenEndpoint: string | undefined,
  roomName: string,
  options: UseTokenOptions = {},
) {
  const [token, setToken] = useState<string | undefined>(undefined)
  const {onAdmissionDenied} = options
  const { query } = useRouter()
  const isHrRoom = options?.userInfo?.v === 'hr'
  const rawPhoneFromQuery = isHrRoom ? (query.user_phone ?? query.phone) : undefined
  const phone = useAppSelector(selectPhone)
  const referrer = useAppSelector(selectReferrer)
  const dispatch = useAppDispatch()
  const abortControllerRef = useRef<AbortController | null>(null)

  // Stable serialized key to avoid re-fetching when the object reference
  // changes but the actual values stay the same.
  const userInfoKey = useMemo(
    () => (options?.userInfo ? JSON.stringify(options.userInfo) : ''),
    [options?.userInfo],
  )

  useEffect(() => {
    if (tokenEndpoint === undefined) {
      throw Error('token endpoint needs to be defined')
    }

    if (!options?.userInfo?.identity || !roomName) {
      return
    }

    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    const tokenFetcher = async () => {
      log.debug('fetching token')

      const isElevatedParticipant = ['roomAdmin', 'isCurator', 'agent', 'recorder'].some((key) => {
        const value = options?.userInfo?.[key]

        return value === true || value === 'true'
      })

      const userInfoParams = Object.entries(options?.userInfo || {}).reduce<Record<string, string>>(
        (acc, [key, value]) => {
          if (value !== null && value !== undefined && (!isHrRoom || key !== 'phone')) {
            acc[key] = String(value)
          }

          return acc
        },
        {},
      )

      const phoneFromUserInfo = String(options?.userInfo?.phone || '').trim()
      const phoneFromQuery =
        isHrRoom && !Array.isArray(rawPhoneFromQuery) ? String(rawPhoneFromQuery || '').trim() : ''
      const phoneFromStore = String(phone || '').trim()
      const tokenPhone = (!isHrRoom || !isElevatedParticipant)
        ? [phoneFromUserInfo, phoneFromQuery, phoneFromStore].find((value) => {
          const digitsCount = value.replace(/\D/g, '').length

          return isHrRoom ? digitsCount === 11 : digitsCount >= 11
        })
        : undefined

      if (isHrRoom && phoneFromStore !== (tokenPhone || '')) {
        dispatch(setPhone(tokenPhone || ''))

        return
      }

      const params = new URLSearchParams({
        roomName,
        ...userInfoParams,
        ...(tokenPhone && { phone: tokenPhone }),
        ...(referrer !== null && referrer !== undefined && { referrer }),
      })

      try {
        const res = await fetch(`${tokenEndpoint}?${params.toString()}`, {
          signal: controller.signal,
        })

        if (!res.ok) {
          const error = await res.json().catch(() => ({}))
          if (!controller.signal.aborted && error.reason === ORGANIZER_VERIFICATION_REQUIRED) {
            requireOrganizerVerification(roomName)
            dispatch(clearAccessToken())
            dispatch(setIsAdmin(false))
            dispatch(setStatus('prejoin'))
            return
          }
          const failure = getInterviewAdmissionFailure(error)
          if (!controller.signal.aborted && failure) onAdmissionDenied?.(failure)
          log.error(
            `Could not fetch token. Server responded with status ${res.status}: ${res.statusText}`,
          )

          return
        }

        const { accessToken } = await res.json()
        if (controller.signal.aborted) return

        dispatch(setAccessToken(accessToken))
        setToken(accessToken)
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          log.error('Error fetching token', e)
          if (shouldCheckInterviewLimit(options?.userInfo || {})) onAdmissionDenied?.('check_unavailable')
        }
      }
    }

    tokenFetcher()

    return () => {
      controller.abort()
    }
  }, [
    tokenEndpoint,
    roomName,
    userInfoKey,
    phone,
    referrer,
    dispatch,
    isHrRoom,
    rawPhoneFromQuery,
    options?.userInfo,
    onAdmissionDenied,
  ])

  return token
}
